/**
 * The consumer API's paths, limits and shapes (QZ-D120). Its routes are server-to-server and
 * read approved tasks only (QZ-D24).
 */

import { z } from 'zod';
import { tagNameSchema } from '../tag/tag';
import {
  ageGroupSchema,
  ageGroupsSchema,
  difficultySchema,
  languageSchema,
  numericAnswerSchema,
  taskIdSchema,
  taskTypeSchema,
  taskTypesSchema,
  tagsSchema,
  textSchema,
} from '../task/task';

/** The random-tasks path (QZ-D120). */
export const CONSUMER_TASKS_PATH = '/consumer/tasks';

/** The document-search path (QZ-D120). */
export const CONSUMER_DOCUMENTS_PATH = '/consumer/documents';

/** The tag-list path (QZ-D120). */
export const CONSUMER_TAGS_PATH = '/consumer/tags';

/** `GET /consumer/tasks`'s default `count` when omitted (QZ-D122). */
export const CONSUMER_TASK_COUNT_DEFAULT = 10;

/** The most tasks `GET /consumer/tasks` answers; GPS Quiz's most tasks per quiz (QZ-D122). */
export const CONSUMER_TASK_COUNT_MAX = 30;

/** The most task ids `exclude` may list (QZ-D125). */
export const CONSUMER_EXCLUDE_MAX = 100;

/** The most tag names `tags` may list (TMA-D4). */
export const CONSUMER_TAGS_MAX = 50;

/** The shortest `search` that `GET /consumer/documents` accepts (QZ-D126). */
export const CONSUMER_SEARCH_MIN_LENGTH = 2;

/** The most documents `GET /consumer/documents` answers (QZ-D126). */
export const CONSUMER_DOCUMENT_HITS_MAX = 20;

/**
 * The query string accepted by `GET /consumer/tasks` (QZ-D122, QZ-D125, QZ-D132, TMA-D4). Unlike
 * `listTasksQuerySchema`, nothing calls `.omit` on this schema, so a `superRefine` is fine here.
 */
export const consumerTasksQuerySchema = z
  .object({
    language: languageSchema,
    count: z.coerce
      .number()
      .int()
      .min(1)
      .max(CONSUMER_TASK_COUNT_MAX)
      .default(CONSUMER_TASK_COUNT_DEFAULT),
    // A comma-separated list of tag names, any of which matches (QZ-D132). Absent parses to
    // undefined; an empty or all-blank value parses to [], which filters nothing, like `exclude`.
    // `.optional()` comes last so the key stays optional in the parsed type.
    tags: z
      .string()
      .transform((raw, ctx) => {
        const names: string[] = [];
        for (const part of raw.split(',')) {
          const trimmed = part.trim();
          if (trimmed === '') {
            continue;
          }
          const parsed = tagNameSchema.safeParse(trimmed);
          if (!parsed.success) {
            ctx.addIssue({ code: 'custom', message: `Not a tag name: ${trimmed}` });
            return z.NEVER;
          }
          if (names.includes(parsed.data)) {
            ctx.addIssue({ code: 'custom', message: 'A tag is listed more than once.' });
            return z.NEVER;
          }
          names.push(parsed.data);
        }
        if (names.length > CONSUMER_TAGS_MAX) {
          ctx.addIssue({ code: 'custom', message: `At most ${CONSUMER_TAGS_MAX} tags.` });
          return z.NEVER;
        }
        return names;
      })
      .optional(),
    ageGroup: ageGroupSchema.optional(),
    taskType: taskTypeSchema.optional(),
    difficulty: difficultySchema.optional(),
    document: textSchema.max(200).optional(),
    section: textSchema.max(200).optional(),
    // A comma-separated list of task ids to leave out (QZ-D125); absent means none.
    exclude: z
      .string()
      .optional()
      .transform((raw, ctx) => {
        if (raw === undefined) {
          return [] as string[];
        }
        const ids: string[] = [];
        for (const part of raw.split(',')) {
          const trimmed = part.trim();
          if (trimmed === '') {
            continue;
          }
          const parsed = taskIdSchema.safeParse(trimmed);
          if (!parsed.success) {
            ctx.addIssue({ code: 'custom', message: `Not a task id: ${trimmed}` });
            return z.NEVER;
          }
          ids.push(parsed.data);
        }
        if (ids.length > CONSUMER_EXCLUDE_MAX) {
          ctx.addIssue({ code: 'custom', message: `At most ${CONSUMER_EXCLUDE_MAX} task ids.` });
          return z.NEVER;
        }
        return ids;
      }),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.section !== undefined && value.document === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['section'],
        message: 'A section needs a document.',
      });
    }
  });
export type ConsumerTasksQuery = z.output<typeof consumerTasksQuerySchema>;

/** One multiple-choice option on a consumer task (QZ-D124). */
export const consumerChoiceSchema = z
  .object({
    text: textSchema,
    correct: z.boolean(),
  })
  .strip();
export type ConsumerChoice = z.infer<typeof consumerChoiceSchema>;

/** Where a consumer task's content came from (QZ-D124); absent for an open-web task. No `kind`. */
export const consumerSourceSchema = z.union([
  z
    .object({
      document: textSchema,
      section: textSchema.max(200).optional(),
      page: z.number().int().positive().optional(),
    })
    .strict(),
  z
    .object({
      url: z.url({ protocol: /^https?$/ }),
    })
    .strict(),
]);
export type ConsumerSource = z.infer<typeof consumerSourceSchema>;

/**
 * One task as a consumer receives it (QZ-D124): one language's wording and its answer data,
 * with the choices shuffled and marked, and everything review-only left out.
 */
export const consumerTaskSchema = z
  .object({
    taskId: taskIdSchema,
    taskTypes: taskTypesSchema,
    difficulty: difficultySchema,
    tags: tagsSchema,
    ageGroups: ageGroupsSchema,
    language: languageSchema,
    text: textSchema,
    choices: z.array(consumerChoiceSchema).min(2).max(6).optional(),
    freetextAnswer: textSchema.max(60).optional(),
    items: z.array(textSchema).min(2).max(6).optional(),
    numericAnswer: numericAnswerSchema.optional(),
    source: consumerSourceSchema.optional(),
  })
  .strip()
  .superRefine((task, ctx) => {
    if (task.choices !== undefined) {
      const correctCount = task.choices.filter((choice) => choice.correct).length;
      if (correctCount !== 1) {
        ctx.addIssue({
          code: 'custom',
          path: ['choices'],
          message: 'Exactly one choice is correct.',
        });
      }
    }
  });
export type ConsumerTask = z.infer<typeof consumerTaskSchema>;

/** The body returned by `GET /consumer/tasks` (QZ-D122). */
export const consumerTasksResponseSchema = z
  .object({
    tasks: z.array(consumerTaskSchema).max(CONSUMER_TASK_COUNT_MAX),
  })
  .strip();
export type ConsumerTasksResponse = z.infer<typeof consumerTasksResponseSchema>;

/** The query string accepted by `GET /consumer/documents` (QZ-D126). */
export const consumerDocumentsQuerySchema = z
  .object({
    search: z.string().trim().min(CONSUMER_SEARCH_MIN_LENGTH).max(200),
  })
  .strict();
export type ConsumerDocumentsQuery = z.infer<typeof consumerDocumentsQuerySchema>;

/** One document's section as a consumer receives it (QZ-D126); only sections with approved tasks. */
export const consumerSectionSchema = z
  .object({
    name: textSchema,
    approved: z.number().int().min(1),
  })
  .strip();
export type ConsumerSection = z.infer<typeof consumerSectionSchema>;

/** One document as a consumer receives it (QZ-D126); only documents with approved tasks. */
export const consumerDocumentSchema = z
  .object({
    document: textSchema,
    approved: z.number().int().min(1),
    sections: z.array(consumerSectionSchema),
  })
  .strip();
export type ConsumerDocument = z.infer<typeof consumerDocumentSchema>;

/**
 * The body returned by `GET /consumer/documents` (QZ-D126): hits ordered by
 * `compareSectionNames` on `document`, each one's `sections` ordered by `sortSections`, as
 * `listDocumentsResponseSchema` says. The schema itself does not sort or check the order.
 */
export const consumerDocumentsResponseSchema = z
  .object({
    documents: z.array(consumerDocumentSchema).max(CONSUMER_DOCUMENT_HITS_MAX),
  })
  .strip();
export type ConsumerDocumentsResponse = z.infer<typeof consumerDocumentsResponseSchema>;
